import { FileJson } from "lucide-react";
import type { PipelineStep, StepArtifact, StepEvent } from "@/lib/api";

/**
 * What ARCH-8 opens a step to show: its own facts, then the bodies of what it was given
 * and what it produced. Shared between the Debug tab (a finished run, every step at
 * once) and the processing panel's dev-mode expansion (a run still in flight, one step
 * opened at a time) — the same answer to "what ran, on what, and what came out", asked
 * from two different moments in a run's life.
 */
export function StepDetails({
  step,
  artifacts,
  problem,
}: {
  step: PipelineStep;
  artifacts: StepArtifact[] | null;
  problem: string | null;
}) {
  return (
    <div className="border-t border-[var(--border-subtle)] px-[var(--space-4)] py-[var(--space-3)]">
      {step.error && (
        <p className="mb-[var(--space-3)] text-[length:var(--text-xs)] text-[var(--warning)]">{step.error}</p>
      )}

      <dl className="mb-[var(--space-3)] grid grid-cols-[auto_1fr] gap-x-[var(--space-4)] gap-y-1">
        <Fact label="Lease" value={step.leaseExpiresAt ?? "not held"} />
        {step.model && <Fact label="Model" value={step.model} />}
        {step.failureCode && <Fact label="Reason" value={step.failureCode} />}
      </dl>

      {step.history && step.history.length > 0 && <History events={step.history} />}

      {problem && <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">{problem}</p>}

      {artifacts === null && !problem && (
        <p className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">Loading…</p>
      )}

      {artifacts?.length === 0 && (
        <p className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          Nothing stored for this step yet.
        </p>
      )}

      {artifacts?.map((artifact) => <Artifact key={artifact.id} artifact={artifact} />)}
    </div>
  );
}

/**
 * One stored artifact.
 *
 * Artifacts are append-only, so a requeued step has more than one of each direction and
 * they are listed newest first — what a *failed* attempt produced is exactly what
 * somebody opening this tab is looking for.
 */
function Artifact({ artifact }: { artifact: StepArtifact }) {
  return (
    <div className="mt-[var(--space-2)] rounded-[var(--radius-md)] bg-[var(--surface-1)] p-[var(--space-3)]">
      <p className="flex items-center gap-[var(--space-2)] text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        <FileJson size={12} strokeWidth={2} aria-hidden />
        {artifact.direction}
        {artifact.sizeBytes !== null && <span className="normal-case">· {bytes(artifact.sizeBytes)}</span>}
      </p>

      {artifact.content !== null ? (
        <>
          <pre className="mt-[var(--space-2)] max-h-64 overflow-auto whitespace-pre-wrap break-all font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] leading-relaxed text-[var(--text-secondary)]">
            {pretty(artifact.content)}
          </pre>
          {artifact.truncated && (
            <p className="mt-[var(--space-1)] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
              Shown up to the preview limit — the stored file goes on past this.
            </p>
          )}
        </>
      ) : (
        <p className="mt-[var(--space-2)] break-all font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-secondary)]">
          {artifact.storagePath}
        </p>
      )}
    </div>
  );
}

/**
 * Every attempt at the step, oldest first (0068). The error above is only the last
 * attempt's; "why did this take three tries" needs the three, and which machine held
 * each one.
 */
function History({ events }: { events: StepEvent[] }) {
  return (
    <div className="mb-[var(--space-3)]">
      <p className="mb-[var(--space-1)] text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        Attempts
      </p>
      <ol aria-label="Attempts" className="space-y-1">
        {events.map((event, index) => (
          <li
            key={`${event.occurredAt}-${index}`}
            className="font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-secondary)]"
          >
            <span className="text-[var(--text-muted)]">#{event.attempt}</span> {describe(event)}
            {event.error && event.event !== "done" && (
              <span className="block pl-[var(--space-4)] text-[var(--warning)]">{event.error}</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

const eventWords: Record<StepEvent["event"], string> = {
  claimed: "claimed",
  running: "running",
  done: "done",
  retried: "failed, retrying",
  requeued: "put back on the queue",
  failed: "failed",
  cancelled: "cancelled",
  restarted: "restarted",
};

function describe(event: StepEvent): string {
  const who = event.workerName ?? event.workerId?.slice(0, 8);
  const what = eventWords[event.event] ?? event.event;
  const code = event.failureCode && event.event !== "done" ? ` (${event.failureCode})` : "";
  const on = who && (event.event === "claimed" || event.event === "retried") ? ` on ${who}` : "";

  return `${time(event.occurredAt)} ${what}${on}${code}`;
}

function time(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleTimeString();
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        {label}
      </dt>
      <dd className="font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-secondary)]">
        {value}
      </dd>
    </>
  );
}

/** Indented if it parses, verbatim if it does not — a body that cannot be read is
 * itself the thing worth looking at. */
function pretty(content: string): string {
  try {
    return JSON.stringify(JSON.parse(content), null, 2);
  } catch {
    return content;
  }
}

function bytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
